/**
 * What the crucible page shows, from the state its hooks report:
 * - `run`: the run is under way on the page's scene (RunView).
 * - `deciding-entry`: not yet known whether anybody gathers, so a loading
 *   view, never a gathering that flashes up first.
 * - `direct-entry`: nobody gathers (a solo direct-entry run): the
 *   before-cutscene, then loading until the run takes over.
 * - `gathering`: the party gathers, readies up and counts down to entry.
 */
export type CruciblePagePhase<TSession> =
    | { readonly kind: 'run'; readonly session: TSession }
    | { readonly kind: 'deciding-entry' }
    | { readonly kind: 'direct-entry' }
    | { readonly kind: 'gathering' };

export interface CruciblePagePhaseInput<TSession> {
    /** The run's connection is in hand and the run has taken over the scene. */
    readonly isRunStarted: boolean;
    /** The page's game session, once started. */
    readonly session: TSession | undefined;
    /** Whether this is a direct-entry run is still being worked out. */
    readonly isDirectEntryLoading: boolean;
    readonly isDirectEntry: boolean;
}

/** The page's one phase; every input combination maps to exactly one. */
export function resolveCruciblePagePhase<TSession>({
    isRunStarted,
    session,
    isDirectEntryLoading,
    isDirectEntry,
}: CruciblePagePhaseInput<TSession>): CruciblePagePhase<TSession> {
    if (isRunStarted && session !== undefined) {
        return { kind: 'run', session };
    }
    if (isDirectEntryLoading) {
        return { kind: 'deciding-entry' };
    }
    return { kind: isDirectEntry ? 'direct-entry' : 'gathering' };
}
